import React, { useRef, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

export interface LineDataset {
  label: string;
  data: number[];
  borderColor: string;
  backgroundColor: string;
  fill?: boolean;
  tension?: number;
  yAxisID?: string;
}

interface LineChartWidgetProps {
  labels: string[];
  datasets: LineDataset[];
  title?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  height?: number;
  dualAxis?: boolean;
  yAxisLabelRight?: string;
  hideLegend?: boolean;
}

const LineChartWidget: React.FC<LineChartWidgetProps> = ({
  labels,
  datasets,
  title,
  xAxisLabel = "Date",
  yAxisLabel = "Count",
  height = 300,
  dualAxis = false,
  yAxisLabelRight = "Count",
  hideLegend = false,
}) => {
  const chartRef = useRef<ChartJS<"line">>(null);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: datasets.map((dataset) => ({
        label: dataset.label,
        data: dataset.data,
        borderColor: dataset.borderColor,
        backgroundColor: dataset.backgroundColor,
        fill: dataset.fill ?? false,
        tension: dataset.tension ?? 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
        ...(dataset.yAxisID && { yAxisID: dataset.yAxisID }),
      })),
    }),
    [labels, datasets],
  );

  const options: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: hideLegend
          ? { display: false }
          : {
              position: "top" as const,
              labels: {
                usePointStyle: true,
                padding: 20,
              },
            },
        title: {
          display: !!title,
          text: title || "",
          font: {
            size: 16,
            weight: "bold",
          },
          padding: {
            bottom: 20,
          },
        },
        tooltip: {
          mode: "index" as const,
          intersect: false,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleFont: {
            size: 14,
          },
          bodyFont: {
            size: 13,
          },
          padding: 12,
          cornerRadius: 8,
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: xAxisLabel,
            font: {
              size: 12,
              weight: "bold",
            },
          },
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 15,
          },
        },
        y: {
          type: "linear" as const,
          display: true,
          position: "left" as const,
          title: {
            display: true,
            text: yAxisLabel,
            font: {
              size: 12,
              weight: "bold",
            },
          },
          beginAtZero: true,
          grid: {
            color: "rgba(0, 0, 0, 0.1)",
          },
          ticks: {
            precision: 0,
          },
        },
        ...(dualAxis && {
          y1: {
            type: "linear" as const,
            display: true,
            position: "right" as const,
            title: {
              display: true,
              text: yAxisLabelRight,
              font: {
                size: 12,
                weight: "bold",
              },
            },
            beginAtZero: true,
            grid: {
              drawOnChartArea: false,
            },
            ticks: {
              precision: 0,
            },
          },
        }),
      },
      interaction: {
        mode: "nearest" as const,
        axis: "x" as const,
        intersect: false,
      },
    }),
    [title, xAxisLabel, yAxisLabel, dualAxis, yAxisLabelRight, hideLegend],
  );

  return (
    <div style={{ height: `${height}px`, width: "100%" }}>
      <Line ref={chartRef} data={chartData} options={options} />
    </div>
  );
};

export default LineChartWidget;
